import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import EditBudgetForm from 'components/Budgets/BudgetList/EditBudgetForm';
import EditIncomeForm from 'components/incomes/EditIncomeForm';
import { useAppSelector } from 'core/hooks/hooks';
import { useNotificationContext } from 'core/providers/NotificationContextProvider';
import {
  setBudgetMonth,
  setMonthBudget
} from 'core/store/actions/budgetMonth.actions';
import { budgetsSelector } from 'core/store/selectors/budgets.selector';
import { expensesSelector } from 'core/store/selectors/expenses.selector';
import { incomesSelector } from 'core/store/selectors/incomes.selector';
import { budgetMonthSelector } from 'core/store/selectors/monthBudget.selector';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useContext, useEffect, useMemo, useState } from 'react';
import BudgetMonthAccumulationsPanel from './BudgetMonthAccumulationsPanel';
import BudgetMonthBudgetsPanel from './BudgetMonthBudgetsPanel';
import { BudgetMonthContext } from './BudgetMonthContextProvider';
import BudgetMonthCurrency from './BudgetMonthCurrency';
import BudgetMonthEditingAlert from './BudgetMonthEditingAlert';
import BudgetMonthIncomesPanel from './BudgetMonthIncomesPanel';
import BudgetMonthLoadingSkeleton from './BudgetMonthLoadingSkeleton';
import BudgetMonthSetupAlert from './BudgetMonthSetupAlert';
import { BudgetMonthSummary } from './BudgetMonthSummary';
import MonthCurrencyLabel from './MonthCurrencyLabel';

const BudgetMonth = () => {
  const { lastAction: budgetLastAction, error: errorLastAction } =
    useAppSelector(budgetsSelector);
  const { showNotification } = useNotificationContext();
  const { expenses, isLoading: expensesLoading } = useAppSelector(
    expensesSelector()
  );
  const { incomes, isLoading: incomesLoading } =
    useAppSelector(incomesSelector);
  const { budgets, isLoading: budgetsLoading } =
    useAppSelector(budgetsSelector);
  const budgetMonth = useAppSelector(periodSelector);
  const {
    budgetMonthCreated,
    lastAction: budgetMonthLastAction,
    error: budgetMonthError,
    isLoading: budgetMonthLoading
  } = useAppSelector(budgetMonthSelector(budgetMonth));
  const { isEditing, setIsEditing, savedCurrency } =
    useContext(BudgetMonthContext);
  const [incomeFormOpen, setIncomeFormOpen] = useState<boolean>(false);
  const [budgetFormOpen, setBudgetFormOpen] = useState<boolean>(false);

  const isLoading =
    expensesLoading || incomesLoading || budgetsLoading || budgetMonthLoading;

  const filteredBudgets = useMemo(
    () => budgets.filter(b => !b.accumulation),
    [budgets]
  );

  useEffect(() => {
    if (
      budgetLastAction === setMonthBudget.fulfilled.type ||
      budgetMonthLastAction === setBudgetMonth.fulfilled.type
    ) {
      showNotification({
        message: `Month budget has been updated`
      });
    }
    if (
      budgetLastAction === setMonthBudget.rejected.type ||
      budgetMonthLastAction === setBudgetMonth.rejected.type
    ) {
      showNotification({
        isError: true,
        message: `Error: ${errorLastAction || budgetMonthError}`
      });
    }
  }, [
    budgetLastAction,
    budgetMonthLastAction,
    errorLastAction,
    budgetMonthError
  ]);

  const handleCloseBudgetMonth = () => {};

  return (
    <Paper sx={{ width: '100%', p: 3, mb: 2 }}>
      {isLoading && <BudgetMonthLoadingSkeleton />}
      {!isLoading && (
        <Grid container spacing={0}>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              borderRight: {
                xs: 'none',
                md: '1px solid #e0e0e0'
              },
              pr: {
                xs: 0,
                md: 4
              }
            }}
          >
            {
              <Box
                sx={{
                  mb: 1,
                  display: 'flex',
                  justifyContent:
                    !isEditing && budgetMonthCreated
                      ? 'space-between'
                      : 'flex-start'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ flexGrow: 1, fontWeight: 500 }}
                  >
                    {new Date(
                      budgetMonth.year,
                      budgetMonth.month
                    ).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>
                  {!isEditing && <MonthCurrencyLabel />}
                </Box>

                {!isEditing && budgetMonthCreated && (
                  <Button variant="outlined" onClick={() => setIsEditing(true)}>
                    Edit Month Budgets
                  </Button>
                )}
              </Box>
            }
            {!budgetMonthCreated && !isEditing && <BudgetMonthSetupAlert />}
            {isEditing && (
              <>
                <Box sx={{ mb: 2 }}>
                  <BudgetMonthEditingAlert />
                </Box>
                <BudgetMonthCurrency />
              </>
            )}

            <BudgetMonthIncomesPanel
              incomes={incomes}
              onCreateIncome={() => setIncomeFormOpen(true)}
            />
            <BudgetMonthBudgetsPanel
              budgets={filteredBudgets}
              expenses={expenses}
              onCreateBudget={() => setBudgetFormOpen(true)}
            />
            <BudgetMonthAccumulationsPanel budgets={budgets} />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ pl: { xs: 0, md: 4 }, mt: { xs: 4, md: 0 } }}
          >
            <Box>
              <BudgetMonthSummary />
            </Box>
          </Grid>
        </Grid>
      )}
      <EditIncomeForm
        isOpen={incomeFormOpen}
        closePanel={() => setIncomeFormOpen(false)}
      />
      <EditBudgetForm
        isOpen={budgetFormOpen}
        closePanel={() => setBudgetFormOpen(false)}
      />
    </Paper>
  );
};

export default BudgetMonth;
